<template>
  <div>
    <div class="mb-2" v-if="!hasRequiredFilters">
      {{ $t("stock_management.storage_losses.select_filters_first") }}
    </div>
    <DxDataGrid
      ref="rowsGrid"
      :data-source="rows"
      :show-borders="true"
      :column-auto-width="true"
      :remote-operations="false"
      :element-attr="gridAttributes"
      key-expr="uuid"
      :editing="{ mode: 'cell', allowUpdating: true }"
      @editor-preparing="onEditorPreparing"
      @row-updated="onRowUpdated"
      @row-prepared="onRowPrepared"
      @cell-prepared="onCellPrepared"
    >
      <DxScrolling mode="standard" show-scrollbar="always" column-rendering-mode="standard" />
      <DxColumn
        :width="52"
        alignment="center"
        :allow-editing="false"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="historyCell"
      />
      <template #historyCell="{ data }">
        <button v-if="canShowHistoryIcon(data.data)" type="button" class="lot-history-info-btn" @click.stop="openHistory(data.data)">
          <i class="fa-solid fa-circle-info"></i>
        </button>
      </template>
      <DxColumn data-field="product_code" :caption="$t('stock_management.products.code')" :allow-editing="false" />
      <DxColumn data-field="product_name" :caption="$t('common.product')" :allow-editing="false" />
      <DxColumn
        data-field="lot"
        :caption="$t('stock_management.predefined_lot_numbers.title')"
        :allow-editing="false"
        :visible="isLotManagedProduct"
      />
      <DxColumn
        data-field="label_name"
        :caption="$t('stock_management.storage_losses.label')"
        :allow-editing="false"
        :visible="isLotManagedProduct"
      />
      <DxColumn data-field="available_quantity" :caption="$t('stock_management.storage_losses.available_qty')" :allow-editing="false" />
      <DxColumn data-field="measure_unit" :caption="$t('common.measure_unit')" :allow-editing="false" />
      <DxColumn data-field="drying_percent" data-type="number" :caption="$t('stock_management.storage_losses.drying_percent')" />
      <DxColumn data-field="drying_quantity" data-type="number" :caption="$t('stock_management.storage_losses.drying_quantity')" />
      <DxColumn data-field="cleaning_percent" data-type="number" :caption="$t('stock_management.storage_losses.cleaning_percent')" />
      <DxColumn data-field="cleaning_quantity" data-type="number" :caption="$t('stock_management.storage_losses.cleaning_quantity')" />
      <DxColumn data-field="loss_percent" data-type="number" :caption="$t('stock_management.storage_losses.loss_percent')" />
      <DxColumn data-field="loss_quantity" data-type="number" :caption="$t('stock_management.storage_losses.loss_quantity')" />
    </DxDataGrid>
    <LotItemHistory
      :showPopup="historyVisible"
      :lotItem="selectedHistoryRow"
      :productUuid="formData?.product_uuid || ''"
      :warehouseUuid="formData?.warehouse_uuid || ''"
      :warehouseName="formData?.warehouse_name || ''"
      :productName="formData?.product_name || ''"
      :measureUnitSymbol="historyMeasureUnitSymbol"
      :labelUuidFilter="selectedHistoryRow?.label_uuid || ''"
      :labelNameFilter="selectedHistoryRow?.label_name || ''"
      @hidden="closeHistory"
    />
  </div>
</template>

<script>
import { DxDataGrid, DxColumn, DxScrolling } from "devextreme-vue/data-grid";
import { mapActions, mapGetters } from "vuex";
import LotItemHistory from "@/pages/stock-mobile/dialogs/lotItemHistory.vue";
import { format } from "date-fns";

export default {
  name: "StorageLossRows",
  components: { DxDataGrid, DxColumn, DxScrolling, LotItemHistory },
  props: {
    formData: { type: Object, default: () => ({}) },
    quantityDecimalPlaces: { type: Number, default: 4 },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid storage-loss-rows-grid",
      },
      lastEditedFieldByRow: {},
      historyVisible: false,
      selectedHistoryRow: null,
    };
  },
  computed: {
    ...mapGetters({
      getQuantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
    }),
    effectiveQuantityDecimalPlaces() {
      if (Number.isInteger(this.getQuantityDecimalPlaces)) {
        return this.getQuantityDecimalPlaces;
      }
      return Number.isInteger(this.quantityDecimalPlaces) ? this.quantityDecimalPlaces : 4;
    },
    hasRequiredFilters() {
      return !!this.formData?.warehouse_uuid && !!this.formData?.product_uuid;
    },
    rows() {
      return this.formData?.rows || [];
    },
    isLotManagedProduct() {
      return Number(this.formData?.product_lot_management || 0) === 1;
    },
    historyMeasureUnitSymbol() {
      return this.selectedHistoryRow?.measure_unit || "";
    },
  },
  watch: {
    formData: {
      deep: true,
      handler(newVal, oldVal) {
        const relevantChanged =
          newVal?.warehouse_uuid !== oldVal?.warehouse_uuid ||
          newVal?.product_uuid !== oldVal?.product_uuid ||
          newVal?.storage_loss_date !== oldVal?.storage_loss_date ||
          JSON.stringify(newVal?.lots || []) !== JSON.stringify(oldVal?.lots || []) ||
          JSON.stringify(newVal?.label_uuids || []) !== JSON.stringify(oldVal?.label_uuids || []);

        if (relevantChanged) {
          this.fetchRows();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    roundValue(value, decimals = this.effectiveQuantityDecimalPlaces) {
      const effectiveDecimals = Number.isInteger(decimals) ? decimals : this.effectiveQuantityDecimalPlaces;
      return Number(parseFloat(value || 0).toFixed(effectiveDecimals));
    },
    toNumeric(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }

      const normalized = String(value).replace(/\s+/g, "").replace(/,/g, ".");
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    validate() {
      this.$refs.rowsGrid.instance.saveEditData();
      return [];
    },
    async fetchRows() {
      if (!this.hasRequiredFilters) {
        this.$emit("input", { rows: [] });
        return;
      }

      const response = await this.getSmStockMovementItemsAvailableSum({
        product_uuid: this.formData.product_uuid,
        warehouse_uuid: this.formData.warehouse_uuid,
        movement_date: this.formatDateOnly(this.formData.storage_loss_date) || undefined,
        group_by: ["lot", "label_uuid"],
        enablePagination: 0,
        limit: 99999,
      });

      const groups = response?.data?.data || response?.data || [];
      const lotFilter = new Set((this.formData?.lots || []).filter(Boolean));
      const labelFilter = new Set((this.formData?.label_uuids || []).filter(Boolean));

      const filtered = groups.filter((row) => {
        const lotOk = lotFilter.size === 0 || lotFilter.has(row?.lot);
        const labelUuid = row?.group_by?.label_uuid || row?.lot_rows?.[0]?.label_uuid || null;
        const labelOk = labelFilter.size === 0 || (labelUuid && labelFilter.has(labelUuid));
        return lotOk && labelOk;
      });

      const existingByKey = new Map((this.formData.rows || []).map((item) => [item.uuid, item]));

      const mapped = filtered.map((row, idx) => {
        const labelUuid = row?.group_by?.label_uuid || row?.lot_rows?.[0]?.label_uuid || null;
        const uuid = `${row?.lot || "no-lot"}|${labelUuid || "no-label"}|${idx}`;
        const existing = existingByKey.get(uuid);
        const dryingPercent = this.roundValue(existing?.drying_percent || 0, 4);
        const cleaningPercent = this.roundValue(existing?.cleaning_percent || 0, 4);
        const dryingQuantity = this.roundValue(existing?.drying_quantity || 0);
        const cleaningQuantity = this.roundValue(existing?.cleaning_quantity || 0);
        const hasSplitLoss = dryingPercent > 0 || cleaningPercent > 0 || dryingQuantity > 0 || cleaningQuantity > 0;
        const lossQuantity = this.roundValue(existing?.loss_quantity || 0);

        return {
          uuid,
          product_code: this.formData.product_code || "",
          product_name: this.formData.product_name || "",
          stock_movement_item_uuid: null,
          lot: row?.lot || null,
          expiration_date: row?.expiration_date || null,
          label_uuid: labelUuid,
          label_name: row?.lot_rows?.[0]?.label_name || "-",
          available_quantity: this.roundValue(row?.quantity || row?.available_quantity || 0),
          measure_unit: row?.measure_unit?.symbol || "",
          drying_percent: dryingPercent,
          drying_quantity: dryingQuantity,
          cleaning_percent: cleaningPercent,
          cleaning_quantity: cleaningQuantity,
          loss_percent: hasSplitLoss ? this.roundValue(dryingPercent + cleaningPercent, 4) : this.roundValue(existing?.loss_percent || 0),
          loss_quantity: hasSplitLoss ? this.roundValue(dryingQuantity + cleaningQuantity) : lossQuantity,
          lot_management: this.formData?.product_lot_management ?? null,
          lot_rows: row?.lot_rows || [],
        };
      });

      this.$emit("input", { rows: mapped });
    },
    formatDateOnly(value) {
      if (!value) return value;
      if (typeof value === "string") return value;
      return format(value, "yyyy-MM-dd");
    },
    canShowHistoryIcon(row) {
      return Number(row?.lot_management ?? this.formData?.product_lot_management ?? 0) === 1;
    },
    openHistory(row) {
      if (!row) {
        return;
      }
      this.selectedHistoryRow = row;
      this.historyVisible = true;
    },
    closeHistory() {
      this.historyVisible = false;
      this.selectedHistoryRow = null;
    },
    onRowUpdated(e) {
      if (!e?.key) return;

      const currentRow = this.rows.find((item) => item.uuid === e.key) || {};
      const row = {
        ...currentRow,
        ...(e?.data || {}),
      };
      const available = this.toNumeric(row.available_quantity);

      const hasDryingPercent = Object.prototype.hasOwnProperty.call(e.data || {}, "drying_percent");
      const hasDryingQuantity = Object.prototype.hasOwnProperty.call(e.data || {}, "drying_quantity");
      const hasCleaningPercent = Object.prototype.hasOwnProperty.call(e.data || {}, "cleaning_percent");
      const hasCleaningQuantity = Object.prototype.hasOwnProperty.call(e.data || {}, "cleaning_quantity");
      const hasLossPercent = Object.prototype.hasOwnProperty.call(e.data || {}, "loss_percent");
      const hasLossQuantity = Object.prototype.hasOwnProperty.call(e.data || {}, "loss_quantity");
      const hasSplitValues =
        this.toNumeric(row.drying_percent) > 0 ||
        this.toNumeric(row.drying_quantity) > 0 ||
        this.toNumeric(row.cleaning_percent) > 0 ||
        this.toNumeric(row.cleaning_quantity) > 0;

      let changedField = this.lastEditedFieldByRow[e.key] || null;
      if (!changedField) {
        if (hasDryingQuantity && !hasDryingPercent) {
          changedField = "drying_quantity";
        } else if (hasDryingPercent && !hasDryingQuantity) {
          changedField = "drying_percent";
        } else if (hasCleaningQuantity && !hasCleaningPercent) {
          changedField = "cleaning_quantity";
        } else if (hasCleaningPercent && !hasCleaningQuantity) {
          changedField = "cleaning_percent";
        } else if (hasLossQuantity && !hasLossPercent) {
          changedField = "loss_quantity";
        } else if (hasLossPercent && !hasLossQuantity) {
          changedField = "loss_percent";
        }
      }

      row.drying_percent = this.roundValue(this.toNumeric(row.drying_percent), 4);
      row.drying_quantity = this.roundValue(this.toNumeric(row.drying_quantity));
      row.cleaning_percent = this.roundValue(this.toNumeric(row.cleaning_percent), 4);
      row.cleaning_quantity = this.roundValue(this.toNumeric(row.cleaning_quantity));
      row.loss_percent = this.roundValue(this.toNumeric(row.loss_percent));
      row.loss_quantity = this.roundValue(this.toNumeric(row.loss_quantity));

      if (changedField === "drying_percent") {
        row.drying_quantity = this.roundValue((available * row.drying_percent) / 100);
      } else if (changedField === "drying_quantity") {
        row.drying_percent = available > 0 ? this.roundValue((row.drying_quantity / available) * 100, 2) : 0;
      } else if (changedField === "cleaning_percent") {
        row.cleaning_quantity = this.roundValue((available * row.cleaning_percent) / 100);
      } else if (changedField === "cleaning_quantity") {
        row.cleaning_percent = available > 0 ? this.roundValue((row.cleaning_quantity / available) * 100, 2) : 0;
      } else if (changedField === "loss_percent" && !hasSplitValues) {
        row.loss_quantity = this.roundValue((available * row.loss_percent) / 100);
      } else if (changedField === "loss_quantity" && !hasSplitValues) {
        row.loss_percent = available > 0 ? this.roundValue((row.loss_quantity / available) * 100, 2) : 0;
      }

      if (hasSplitValues || ["drying_percent", "drying_quantity", "cleaning_percent", "cleaning_quantity"].includes(changedField)) {
        row.loss_percent = this.roundValue(row.drying_percent + row.cleaning_percent, 4);
        row.loss_quantity = this.roundValue(row.drying_quantity + row.cleaning_quantity);
      }

      if (row.loss_quantity > available) {
        const scale = row.loss_quantity > 0 ? available / row.loss_quantity : 0;
        row.drying_quantity = this.roundValue(row.drying_quantity * scale);
        row.cleaning_quantity = this.roundValue(row.cleaning_quantity * scale);
        row.drying_percent = available > 0 ? this.roundValue((row.drying_quantity / available) * 100, 2) : 0;
        row.cleaning_percent = available > 0 ? this.roundValue((row.cleaning_quantity / available) * 100, 2) : 0;
        row.loss_quantity = this.roundValue(row.drying_quantity + row.cleaning_quantity);
        row.loss_percent = 100;
      }

      if (row.loss_quantity < 0) {
        row.loss_quantity = 0;
        row.loss_percent = 0;
      }

      const rows = [...(this.formData?.rows || [])];
      const rowIndex = rows.findIndex((item) => item.uuid === row.uuid || item.uuid === e.key);

      if (rowIndex >= 0) {
        rows.splice(rowIndex, 1, row);
      } else {
        rows.push(row);
      }

      this.$emit("input", { rows });
      delete this.lastEditedFieldByRow[e.key];
    },
    onEditorPreparing(e) {
      if (e?.parentType !== "dataRow" || !e?.row || !e?.dataField) {
        return;
      }

      const editableFields = [
        "drying_percent",
        "drying_quantity",
        "cleaning_percent",
        "cleaning_quantity",
        "loss_percent",
        "loss_quantity",
      ];
      if (!editableFields.includes(e.dataField)) {
        return;
      }

      this.lastEditedFieldByRow[e.row.key] = e.dataField;
    },
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.rowElement) {
        return;
      }

      const isPositive = Number(e?.data?.loss_quantity || 0) > 0;
      e.rowElement.classList.toggle("loss-row-positive", isPositive);
    },
    onCellPrepared(e) {
      if (e.rowType === "data" && e.column && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.storage-loss-rows-grid {
  height: 100%;
}

.lot-history-info-btn {
  border: none;
  background: transparent;
  color: #2d7fe0;
  cursor: pointer;
  padding: 0;
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.lot-history-info-btn:hover {
  color: #1e5fa8;
}

.lot-history-info-btn .fa-circle-info {
  font-size: 18px;
}

::v-deep .loss-row-positive > td {
  background-color: #579b5a !important;
  color: #ffffff !important;
}
</style>
